import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Stack,
  Chip,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import DevicesIcon from '@mui/icons-material/Devices';
import api from '../../services/api';
import { deviceTokenManager } from '../../utils/deviceToken';

interface TwoFactorVerificationProps {
  open: boolean;
  userId: number;
  method: 'email' | 'sms';
  onSuccess: (response: any) => void;
  onCancel: () => void;
}

const TwoFactorVerification: React.FC<TwoFactorVerificationProps> = ({
  open,
  userId,
  method,
  onSuccess,
  onCancel,
}) => {
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Clear the code and error when dialog opens/closes
  useEffect(() => {
    if (open) {
      setCode('');
      setError('');
      setTrustDevice(false);
    }
  }, [open]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const deviceName = `${navigator.userAgent.split('(')[0].trim()} - ${new Date().toLocaleDateString()}`;
      
      const response = await api.post('/auth/two-factor', {
        user_id: userId,
        code,
        trust_device: trustDevice,
        device_name: deviceName,
      }, {
        params: { action: 'verify' }
      });

      if (response.data.success) {
        // Store device token if provided (in cookie and localStorage)
        if (response.data.data.device_token) {
          deviceTokenManager.setToken(response.data.data.device_token);
        }
        onSuccess(response.data);
      } else {
        setError(response.data.message || 'Invalid code');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;

    setLoading(true);
    setError('');

    try {
      // We need to resend using the original credentials
      // This would be handled by the parent component
      setResendTimer(60); // Prevent spam - 60 second cooldown
      setError('');
    } catch (error: any) {
      setError('Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError('');
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {method === 'email' ? <EmailIcon /> : <SmsIcon />}
          Two-Factor Authentication
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body1">
            A verification code has been sent to your {method === 'email' ? 'email address' : 'phone number'}.
            Please enter the 6-digit code below.
          </Typography>

          <TextField
            fullWidth
            label="Verification Code"
            value={code}
            onChange={handleCodeChange}
            placeholder="000000"
            error={!!error}
            helperText={error}
            disabled={loading}
            autoFocus
            inputProps={{
              maxLength: 6,
              style: { fontSize: '1.5rem', letterSpacing: '0.5rem', textAlign: 'center' }
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                disabled={loading}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <DevicesIcon fontSize="small" />
                <Typography variant="body2">
                  Remember this device for 30 days
                </Typography>
              </Box>
            }
          />

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Button
              onClick={handleResendCode}
              disabled={loading || resendTimer > 0}
              size="small"
            >
              {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
            </Button>
            
            <Chip
              label="Code expires in 10 minutes"
              size="small"
              variant="outlined"
            />
          </Box>

          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleVerify}
          variant="contained"
          disabled={loading || code.length !== 6}
        >
          {loading ? <CircularProgress size={24} /> : 'Verify'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TwoFactorVerification;